import { Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { FC, useCallback, useState } from 'react'
import { Button, InformationModal } from 'utoniq-ui'

import { MembershipSubscriptionPlanDetailFragment } from '@/clients/utoniq-core/schema'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { constants } from '@/config'
import { useMessage, useTranslation } from '@/core'
import { captureError } from '@/core/errors'

import { Action } from './../'

type ChangePlanButtonProps = {
  type: string
  artistId: string
  formattedExpiredAt: string
  diffAmountSubscription: number
  currentPlan?: MembershipSubscriptionPlanDetailFragment
  upgradePlan?: MembershipSubscriptionPlanDetailFragment
  downgradePlan?: MembershipSubscriptionPlanDetailFragment
  changingSubscription: boolean
  onUpgradeSubscriptionPlan: Action['onUpgradeSubscriptionPlan']
  onDowngradeSubscriptionPlan: Action['onDowngradeSubscriptionPlan']
  onPreviewSubscriptionUpdate: Action['onPreviewSubscriptionUpdate']
}

export const ChangePlanButton: FC<ChangePlanButtonProps> = ({
  type,
  artistId,
  formattedExpiredAt,
  diffAmountSubscription,
  currentPlan,
  upgradePlan,
  downgradePlan,
  changingSubscription,
  onUpgradeSubscriptionPlan,
  onDowngradeSubscriptionPlan,
  onPreviewSubscriptionUpdate,
}) => {
  const { t } = useTranslation('membership')
  const { showError } = useMessage()
  const [loading, setLoading] = useState<boolean>(false)
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const handleChangeSubscription = useCallback(async () => {
    if (upgradePlan) {
      await onUpgradeSubscriptionPlan(upgradePlan.planId, artistId).catch(e => {
        captureError(e, 'MembershipTemplate')
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @description
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenPaymentErrorModal(true)
            break
          default:
            showError(new Error(t('error')))
            break
        }
      })
    }
    if (downgradePlan) {
      await onDowngradeSubscriptionPlan(downgradePlan.planId, artistId).catch(
        e => {
          captureError(e, 'MembershipTemplate')
          showError(new Error(t('error')))
        }
      )
    }
    setOpenConfirm(false)
  }, [
    downgradePlan,
    artistId,
    onDowngradeSubscriptionPlan,
    onUpgradeSubscriptionPlan,
    showError,
    t,
    upgradePlan,
  ])

  if (type !== 'stripe' || (!upgradePlan && !downgradePlan)) {
    return null
  }

  return (
    <>
      <Button
        size="large"
        color="inherit"
        sx={{
          width: 240,
          '&.MuiButtonBase-root:hover': { bgcolor: 'grey.50' },
          bgcolor: 'grey.50',
        }}
        loading={loading}
        disabled={loading}
        onClick={async () => {
          setLoading(true)
          if (upgradePlan?.planId) {
            await onPreviewSubscriptionUpdate(artistId, upgradePlan.planId)
          }
          setOpenConfirm(true)
          setLoading(false)
        }}
      >
        {upgradePlan ? t('upgradePlan') : t('downgradePlan')}
      </Button>

      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('confirm')}
        content={
          <Stack spacing={3} sx={{ pb: 7, textAlign: 'left' }}>
            {upgradePlan && (
              <>
                <Typography variant="body2">
                  {t('yearCourseConfirmContent', {
                    amount: numeral(diffAmountSubscription ?? 0).format('0,0'),
                  })}
                </Typography>
                <Typography variant="subtitle1">
                  {t('yearCourseConfirmFee', {
                    amount: upgradePlan.formattedAmount,
                  })}
                </Typography>
              </>
            )}
            {downgradePlan && (
              <>
                <Typography variant="body2">
                  {t('monthCourseConfirmContent')}
                </Typography>
                <Typography variant="subtitle1">
                  {t('monthCourseConfirmFee', {
                    amount: downgradePlan?.formattedAmount,
                    date: formattedExpiredAt,
                  })}
                </Typography>
              </>
            )}
          </Stack>
        }
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ minWidth: 212 }}
            disabled={changingSubscription}
            loading={changingSubscription}
            onClick={handleChangeSubscription}
          >
            {t('change')}
          </Button>
        }
      />
      <ExpiredCardModal
        open={openExpiredCardModal}
        onClose={() => setOpenExpiredCardModal(false)}
      />

      <PaymentErrorModal
        open={openPaymentErrorModal}
        onClose={() => setOpenPaymentErrorModal(false)}
      />

      <Require3DSecureModal
        open={openRequire3DSecureModal}
        onClose={() => setOpenRequire3DSecureModal(false)}
      />
    </>
  )
}
